import { useNuxtApp, useRuntimeConfig } from 'nuxt/app';
import { RuntimeConfig } from 'nuxt/schema';
import { IUserProfile } from '@/models/userProfile.model';
import { AppLoggerClientType, UserActions, IAppLogger } from '@/models/appLogger.model';
import { useUserProfileStore } from '@/store/userProfileStore';

export const appLogger = {
  logEvent: (name: string, attributes?: Record<string, string>) => {
    const logger = getAppLogger();
    if (!logger) return;

    const eventAttributes = getEventAttributes(attributes);

    if (logger.clientType === AppLoggerClientType.grafanaFaro) {
      logger.client.api.pushEvent(name, { ...eventAttributes });
    }
  },

  logError: (error: Error, attributes?: Record<string, string>) => {
    const logger = getAppLogger();
    if (!logger) return;

    const errorAttributes = getEventAttributes(attributes);

    if (logger.clientType === AppLoggerClientType.grafanaFaro) {
      logger.client.api.pushError(error, { ...errorAttributes });
    }
  },

  logMeasurement: (metrics: Record<string, number>, name: string = 'unknown') => {
    const logger = getAppLogger();
    if (!logger) return;

    if (logger.clientType === AppLoggerClientType.grafanaFaro) {
      logger.client.api?.pushMeasurement({ type: name, values: { ...metrics } });
    }
  },

  logPageView: (pageName: string, attributes?: Record<string, string>) => {
    const logger = getAppLogger();
    if (!logger) return;

    const eventAttributes = getEventAttributes(attributes);

    if (logger.clientType === AppLoggerClientType.grafanaFaro) {
      logger.client.api?.pushEvent('pageview', { page: pageName, ...eventAttributes });
    }
  },

  logUserAction: (userAction: UserActions, attributes?: Record<string, string>) => {
    const logger = getAppLogger();
    if (!logger) return;

    const eventAttributes = getEventAttributes(attributes);

    if (logger.clientType === AppLoggerClientType.grafanaFaro) {
      logger.client.api.startUserAction(userAction, { ...eventAttributes });
    }
  },

  setUser: (userProfile: IUserProfile | undefined) => {
    const logger = getAppLogger();
    if (!logger || logger.clientType !== AppLoggerClientType.grafanaFaro) return;

    if (!userProfile) {
      logger.client.api.resetUser();
    }
    else {
      logger.client.api?.setUser({
        id: userProfile.userId,
        userName: userProfile.fullName,
        customerId: userProfile.customerId,
        portalLink: userProfile.portalLink,
        featureFlags: JSON.stringify(userProfile.featureFlags),
      });
    }
  },

  setSessionAttribute: (attributes: Record<string, string>) => {
    const logger = getAppLogger();
    if (!logger || logger.clientType !== AppLoggerClientType.grafanaFaro) return;

    const eventAttributes = getEventAttributes(attributes);

    logger.client.api.setSession({ attributes: { ...eventAttributes } });
  }
};

const getAppLogger = (): IAppLogger | undefined => {
  const runtimeConfig: RuntimeConfig = useRuntimeConfig();

  if (runtimeConfig.public.faroIngestUrl) {
    const { $faroClient } = useNuxtApp() as any;
    return { 
      client: $faroClient, 
      clientType: AppLoggerClientType.grafanaFaro
    };
  }

  return undefined;
};

const getDefaultEventAttributes = (): Record<string, string> => {
  const userProfileStore = useUserProfileStore();
  return {
    userName: userProfileStore.fullName || 'Unknown',
    portalLink: userProfileStore.portalLink || 'Unknown'
  }
};

const getEventAttributes = (attributes?: Record<string, string>): Record<string, string> => {
  const defaultAttributes = getDefaultEventAttributes();
  if (!attributes) return defaultAttributes;

  return {
    ...defaultAttributes,
    ...attributes
  };
};